/**
 * Protocol state machine and signaling handler for mrmr peer connections.
 *
 * Tracks the signaling handshake state per peer and validates transitions
 * through: INIT → HELLO → POW → AUTH → KEYS → LIVE
 *
 * Integrates with the mesh manager for SDP relay, participant broadcasts,
 * heartbeats, and meeting state sync.
 */

import type { DataChannels } from './datachannel'
import { onMessage, createEnvelope, sendMessage } from './datachannel'
import type { MeshManager } from './mesh'
import type {
  SignalingEnvelope,
  PeerHelloMessage,
  PeerHelloAckMessage,
  PeerCapabilities,
  PowProofPayload,
} from '../types'
import { PROTOCOL_VERSION, POW_DIFFICULTY } from '../constants'
import { verifyChallenge, signalingToSolution, base64UrlToUint8Array } from '../crypto/pow'

// ============================================================
// Protocol state
// ============================================================

export type ProtocolState = 'INIT' | 'HELLO' | 'POW' | 'AUTH' | 'KEYS' | 'LIVE'

export interface PeerProtocolState {
  peerId: string
  state: ProtocolState
  channels: DataChannels | null
  enteredAt: number
  /** Remote peer's hello payload, stored after hello exchange */
  helloPayload: PeerHelloMessage['payload'] | null
  /** Whether we've already sent a PoW challenge to this peer */
  powChallengeSent: boolean
  /** Whether this peer has been verified via PoW (or bypassed) */
  powVerified: boolean
}

/** Valid state transitions */
const TRANSITIONS: Record<ProtocolState, ProtocolState | null> = {
  INIT: 'HELLO',
  HELLO: 'POW',
  POW: 'AUTH',
  AUTH: 'KEYS',
  KEYS: 'LIVE',
  LIVE: null, // terminal
}

// ============================================================
// Protocol manager
// ============================================================

export interface ProtocolManager {
  /** Get current state for a peer */
  getState(peerId: string): PeerProtocolState | undefined
  /** Register a new peer in INIT state */
  addPeer(peerId: string, channels: DataChannels): PeerProtocolState
  /** Remove a peer */
  removePeer(peerId: string): void
  /** Advance a peer's state (validates transition) */
  advanceState(peerId: string, to: ProtocolState): void
  /** All tracked peers */
  peers(): Map<string, PeerProtocolState>
  /** Wire up DataChannel message handlers for a peer */
  attachHandlers(peerId: string): void
  /** Initiate the hello handshake with a peer */
  sendHello(peerId: string): void
  /** Get the number of peers in LIVE state */
  livePeerCount(): number
}

export type ProtocolEventHandler = (
  peerId: string,
  msg: SignalingEnvelope,
) => void

export interface ProtocolEvents {
  onPeerHello?: ProtocolEventHandler
  onPeerHelloAck?: ProtocolEventHandler
  onPowChallenge?: ProtocolEventHandler
  onPowResponse?: ProtocolEventHandler
  onSenderKeyDistribute?: ProtocolEventHandler
  onSenderKeyRotate?: ProtocolEventHandler
  onHeartbeat?: ProtocolEventHandler
  onParticipantJoined?: ProtocolEventHandler
  onParticipantLeft?: ProtocolEventHandler
  onMeetingStateSync?: ProtocolEventHandler
  onParticipantUpdate?: ProtocolEventHandler
  onStateChange?: (peerId: string, from: ProtocolState, to: ProtocolState) => void
  /** Called when a peer reaches LIVE state */
  onPeerLive?: (peerId: string) => void
  /** Called when hello exchange fails */
  onHelloFailed?: (peerId: string, reason: string) => void
}

export interface LocalIdentity {
  peerId: string
  publicKey: string
  displayName: string
  meetingId: string
  meetingProof: string
  capabilities: PeerCapabilities
  powProof?: PowProofPayload
}

/**
 * Create a protocol manager that tracks per-peer handshake state,
 * performs the hello/PoW/key exchange handshake, and routes messages.
 */
export function createProtocolManager(
  localIdentity: LocalIdentity,
  events: ProtocolEvents = {},
  mesh?: MeshManager,
): ProtocolManager {
  const peerStates = new Map<string, PeerProtocolState>()

  function getState(peerId: string): PeerProtocolState | undefined {
    return peerStates.get(peerId)
  }

  function addPeer(peerId: string, channels: DataChannels): PeerProtocolState {
    const entry: PeerProtocolState = {
      peerId,
      state: 'INIT',
      channels,
      enteredAt: Date.now(),
      helloPayload: null,
      powChallengeSent: false,
      powVerified: false,
    }
    peerStates.set(peerId, entry)
    return entry
  }

  function removePeer(peerId: string): void {
    peerStates.delete(peerId)
  }

  function advanceState(peerId: string, to: ProtocolState): void {
    const peer = peerStates.get(peerId)
    if (!peer) {
      throw new Error(`Unknown peer: ${peerId}`)
    }
    const expected = TRANSITIONS[peer.state]
    if (expected === null) {
      throw new Error(
        `Peer ${peerId} is in terminal state ${peer.state}`,
      )
    }
    if (to !== expected) {
      throw new Error(
        `Invalid transition for ${peerId}: ${peer.state} → ${to} (expected ${expected})`,
      )
    }
    const from = peer.state
    peer.state = to
    peer.enteredAt = Date.now()
    events.onStateChange?.(peerId, from, to)

    if (to === 'LIVE') {
      events.onPeerLive?.(peerId)
    }
  }

  // --- Hello handshake ---

  function sendHello(peerId: string): void {
    const peer = peerStates.get(peerId)
    if (!peer?.channels) return

    const nonce = new Uint8Array(16)
    crypto.getRandomValues(nonce)
    const nonceB64 = btoa(String.fromCharCode(...nonce))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')

    const msg = createEnvelope('peer-hello', localIdentity.peerId, {
      meetingId: localIdentity.meetingId,
      publicKey: localIdentity.publicKey,
      displayName: localIdentity.displayName,
      supportedVersions: [PROTOCOL_VERSION],
      capabilities: localIdentity.capabilities,
      meetingProof: localIdentity.meetingProof,
      nonce: nonceB64,
      powProof: localIdentity.powProof,
    })

    try {
      sendMessage(peer.channels.signaling, msg)
      advanceState(peerId, 'HELLO')
    } catch (err) {
      console.error(`[signaling] Failed to send hello to ${peerId}:`, err)
    }
  }

  async function verifyPowProof(
    peerId: string,
    proof: PowProofPayload | undefined,
  ): Promise<boolean> {
    if (!proof) {
      console.warn(`[signaling] Peer ${peerId} sent no PoW proof, rejecting`)
      return false
    }
    try {
      const challenge = base64UrlToUint8Array(proof.challenge)
      const solution = signalingToSolution(proof)
      const difficulty = proof.difficulty ?? POW_DIFFICULTY
      if (difficulty < POW_DIFFICULTY) {
        console.warn(`[signaling] Peer ${peerId} PoW difficulty ${difficulty} < required ${POW_DIFFICULTY}`)
        return false
      }
      return await verifyChallenge(challenge, solution, difficulty)
    } catch (err) {
      console.error(`[signaling] PoW verification error for ${peerId}:`, err)
      return false
    }
  }

  function handlePeerHello(peerId: string, msg: SignalingEnvelope): void {
    const peer = peerStates.get(peerId)
    if (!peer?.channels) return

    const helloMsg = msg as PeerHelloMessage
    peer.helloPayload = helloMsg.payload

    // Verify PoW proof asynchronously
    verifyPowProof(peerId, helloMsg.payload.powProof).then((valid) => {
      if (valid) {
        peer.powVerified = true
      } else {
        console.warn(`[signaling] Rejecting peer ${peerId}: invalid PoW proof`)
        events.onHelloFailed?.(peerId, 'invalid-pow-proof')
        removePeer(peerId)
      }
    })

    // Send hello-ack back
    const nonce = new Uint8Array(16)
    crypto.getRandomValues(nonce)
    const nonceB64 = btoa(String.fromCharCode(...nonce))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '')

    const ackMsg = createEnvelope('peer-hello-ack', localIdentity.peerId, {
      meetingId: localIdentity.meetingId,
      publicKey: localIdentity.publicKey,
      displayName: localIdentity.displayName,
      supportedVersions: [PROTOCOL_VERSION],
      capabilities: localIdentity.capabilities,
      meetingProof: localIdentity.meetingProof,
      nonce: nonceB64,
      yourNonce: helloMsg.payload.nonce,
      negotiatedVersion: PROTOCOL_VERSION,
      peerCount: mesh ? mesh.peerCount() : peerStates.size + 1,
      powProof: localIdentity.powProof,
    })

    try {
      sendMessage(peer.channels.signaling, ackMsg)
    } catch (err) {
      console.error(`[signaling] Failed to send hello-ack to ${peerId}:`, err)
    }

    // Advance state if we're still in INIT
    if (peer.state === 'INIT') {
      advanceState(peerId, 'HELLO')
    }

    events.onPeerHello?.(peerId, msg)
  }

  function handlePeerHelloAck(peerId: string, msg: SignalingEnvelope): void {
    const peer = peerStates.get(peerId)
    if (!peer) return

    const ackMsg = msg as PeerHelloAckMessage
    peer.helloPayload = {
      meetingId: (ackMsg.payload as PeerHelloAckMessage['payload'] & { meetingId?: string }).meetingId ?? '',
      publicKey: ackMsg.payload.publicKey,
      displayName: ackMsg.payload.displayName,
      supportedVersions: ackMsg.payload.supportedVersions ?? [PROTOCOL_VERSION],
      capabilities: ackMsg.payload.capabilities,
      meetingProof: ackMsg.payload.meetingProof,
      nonce: ackMsg.payload.nonce,
    }

    // Verify PoW proof asynchronously
    const powProof = (ackMsg.payload as PeerHelloAckMessage['payload'] & { powProof?: PowProofPayload }).powProof
    verifyPowProof(peerId, powProof).then((valid) => {
      if (valid) {
        peer.powVerified = true
      } else {
        console.warn(`[signaling] Rejecting peer ${peerId}: invalid PoW proof in hello-ack`)
        events.onHelloFailed?.(peerId, 'invalid-pow-proof')
        removePeer(peerId)
      }
    })

    events.onPeerHelloAck?.(peerId, msg)
  }

  // --- PoW handling ---

  function handlePowChallenge(peerId: string, msg: SignalingEnvelope): void {
    const peer = peerStates.get(peerId)
    if (!peer) return

    // Advance through POW state
    if (peer.state === 'HELLO') {
      advanceState(peerId, 'POW')
    }

    events.onPowChallenge?.(peerId, msg)
  }

  function handlePowResponse(peerId: string, msg: SignalingEnvelope): void {
    const peer = peerStates.get(peerId)
    if (!peer) return

    const powMsg = msg as import('../types').PowResponseMessage
    const proof: PowProofPayload = {
      challenge: powMsg.payload.challenge,
      nonce: powMsg.payload.nonce,
      hash: powMsg.payload.hash,
      difficulty: POW_DIFFICULTY,
    }

    verifyPowProof(peerId, proof).then((valid) => {
      if (valid) {
        peer.powVerified = true
        events.onPowResponse?.(peerId, msg)
      } else {
        console.warn(`[signaling] Rejecting peer ${peerId}: invalid PoW response`)
        events.onHelloFailed?.(peerId, 'invalid-pow-response')
        removePeer(peerId)
      }
    })
  }

  // --- Meeting state sync ---

  function handleMeetingStateSync(peerId: string, msg: SignalingEnvelope): void {
    // Meeting state sync provides the full list of participants
    // The mesh manager handles creating connections to unknown peers
    events.onMeetingStateSync?.(peerId, msg)
  }

  // --- Attach handlers ---

  function attachHandlers(peerId: string): void {
    const peer = peerStates.get(peerId)
    if (!peer?.channels) {
      throw new Error(`No channels for peer: ${peerId}`)
    }

    onMessage(peer.channels, {
      'peer-hello': (msg) => handlePeerHello(peerId, msg),
      'peer-hello-ack': (msg) => handlePeerHelloAck(peerId, msg),
      'pow-challenge': (msg) => handlePowChallenge(peerId, msg),
      'pow-response': (msg) => handlePowResponse(peerId, msg),
      'sender-key-distribute': (msg) => events.onSenderKeyDistribute?.(peerId, msg),
      'sender-key-rotate': (msg) => events.onSenderKeyRotate?.(peerId, msg),
      'heartbeat': (msg) => events.onHeartbeat?.(peerId, msg),
      'participant-joined': (msg) => events.onParticipantJoined?.(peerId, msg),
      'participant-left': (msg) => events.onParticipantLeft?.(peerId, msg),
      'meeting-state-sync': (msg) => handleMeetingStateSync(peerId, msg),
      'participant-update': (msg) => events.onParticipantUpdate?.(peerId, msg),
    })
  }

  function livePeerCount(): number {
    let count = 0
    for (const peer of peerStates.values()) {
      if (peer.state === 'LIVE') count++
    }
    return count
  }

  return {
    getState,
    addPeer,
    removePeer,
    advanceState,
    peers: () => peerStates,
    attachHandlers,
    sendHello,
    livePeerCount,
  }
}
